import { useEffect, useState } from "react";
import { Plus, Shield, Plug, Database, RefreshCw, Award, Trash2, Briefcase, CalendarClock, Wand2, Mail, Send, FileCheck2, ListChecks, BarChart3 } from "lucide-react";
import { api, roleLabel, shortDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const CORE_ROLES = ["admin", "rcsm", "cnat", "hr", "employee"];

export default function Administration() {
  const [roles, setRoles] = useState([]);
  const [jobRoles, setJobRoles] = useState([]);
  const [blockTypes, setBlockTypes] = useState([]);
  const [skills, setSkills] = useState([]);
  const [eleap, setEleap] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [digests, setDigests] = useState(null);
  const [sending, setSending] = useState(false);
  const [certTypes, setCertTypes] = useState([]);
  const [onboardingItems, setOnboardingItems] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [sendingComp, setSendingComp] = useState(false);
  const [inputs, setInputs] = useState({ role: "", jobRole: "", blockType: "", skill: "", certType: "", onboardingItem: "" });

  const set = (k, v) => setInputs((s) => ({ ...s, [k]: v }));
  const loadAll = () => {
    api.get("/roles").then((r) => setRoles(r.data));
    api.get("/job-roles").then((r) => setJobRoles(r.data));
    api.get("/block-types").then((r) => setBlockTypes(r.data));
    api.get("/skills").then((r) => setSkills(r.data));
    api.get("/cert-types").then((r) => setCertTypes(r.data));
    api.get("/onboarding-items").then((r) => setOnboardingItems(r.data));
    api.get("/integrations/eleap").then((r) => setEleap(r.data));
    api.get("/digests/preview").then((r) => setDigests(r.data)).catch(() => {});
    api.get("/compliance/preview").then((r) => setCompliance(r.data)).catch(() => {});
  };
  const sendCompliance = async () => {
    setSendingComp(true);
    try { const { data } = await api.post("/compliance/send-now"); toast.success(`Sent to ${data.sent} admin(s)`); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setSendingComp(false); }
  };
  const addOnboarding = async () => {
    const label = inputs.onboardingItem?.trim();
    if (!label) return;
    try { await api.post("/onboarding-items", { label }); toast.success("Added"); set("onboardingItem", ""); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
  };
  useEffect(() => { loadAll(); }, []);

  const add = async (url, name, key) => {
    if (!name.trim()) return;
    try { await api.post(url, { name: name.trim() }); toast.success("Added"); set(key, ""); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
  };
  const removeEnc = async (url) => { try { await api.delete(url); loadAll(); } catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); } };

  const syncEleap = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/integrations/eleap/sync"); toast.success(`Synced ${data.synced} employees (simulated)`); loadAll(); }
    catch (e) { toast.error("Sync failed"); } finally { setSyncing(false); }
  };
  const generateNow = async () => {
    setGenerating(true);
    try { const { data } = await api.post("/work/generate-now"); toast.success(`Generated ${data.created} recurring tasks`); }
    catch (e) { toast.error("Generation failed"); } finally { setGenerating(false); }
  };
  const sendDigests = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/digests/send-now");
      toast.success(`Sent ${data.sent} digest email(s) to managers`);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send digests"); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Administration</h1>
        <p className="mt-1 text-sm text-slate-500">System configuration, roles, work types and integrations.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card icon={Shield} title="User roles">
          <Chips items={roles.map((r) => ({ id: r, label: roleLabel(r), deletable: !CORE_ROLES.includes(r) }))} onDelete={(id) => removeEnc(`/roles/${id}`)} testid="role-chip" />
          <AddRow placeholder="Add a role (e.g. Finance)" value={inputs.role} onChange={(v) => set("role", v)} onAdd={() => add("/roles", inputs.role, "role")} testid="role" />
          <p className="mt-2 text-xs text-slate-400">Core roles cannot be deleted. Roles in use cannot be removed.</p>
        </Card>

        <Card icon={Briefcase} title="Employee job roles">
          <Chips items={jobRoles.map((r) => ({ id: r, label: r, deletable: true }))} onDelete={(id) => removeEnc(`/job-roles/${encodeURIComponent(id)}`)} testid="jobrole-chip" />
          <AddRow placeholder="Add a job role" value={inputs.jobRole} onChange={(v) => set("jobRole", v)} onAdd={() => add("/job-roles", inputs.jobRole, "jobRole")} testid="jobrole" />
          <p className="mt-2 text-xs text-slate-400">These pull through to the Add Employee form.</p>
        </Card>

        <Card icon={CalendarClock} title="Calendar block types">
          <Chips items={blockTypes.map((r) => ({ id: r, label: r, deletable: true }))} onDelete={(id) => removeEnc(`/block-types/${encodeURIComponent(id)}`)} testid="blocktype-chip" />
          <AddRow placeholder="Add a block type" value={inputs.blockType} onChange={(v) => set("blockType", v)} onAdd={() => add("/block-types", inputs.blockType, "blockType")} testid="blocktype" />
        </Card>

        <Card icon={Award} title="Skills catalogue">
          <Chips items={skills.map((s) => ({ id: s.id, label: s.name, deletable: true }))} onDelete={(id) => removeEnc(`/skills/${id}`)} testid="skill-chip" />
          <AddRow placeholder="Add a skill" value={inputs.skill} onChange={(v) => set("skill", v)} onAdd={() => add("/skills", inputs.skill, "skill")} testid="skill" />
        </Card>

        <Card icon={FileCheck2} title="Certificate types">
          <Chips items={certTypes.map((c) => ({ id: c.id, label: c.name, deletable: true }))} onDelete={(id) => removeEnc(`/cert-types/${id}`)} testid="certtype-chip" />
          <AddRow placeholder="Add a certificate type" value={inputs.certType} onChange={(v) => set("certType", v)} onAdd={() => add("/cert-types", inputs.certType, "certType")} testid="certtype" />
          <p className="mt-2 text-xs text-slate-400">These become columns in the Training Matrix with expiry tracking.</p>
        </Card>

        <Card icon={ListChecks} title="New starter onboarding">
          <p className="mb-3 text-sm text-slate-500">The checklist a trainer works through for every new starter. Edit or add items here.</p>
          <Chips items={onboardingItems.map((i) => ({ id: i.id, label: i.label, deletable: true }))} onDelete={(id) => removeEnc(`/onboarding-items/${id}`)} testid="onboarding-chip" />
          <div className="mt-4 flex gap-2">
            <Input data-testid="new-onboarding-input" placeholder="Add a checklist item (e.g. Fire safety training)" value={inputs.onboardingItem} onChange={(e) => set("onboardingItem", e.target.value)} onKeyDown={(e) => e.key === "Enter" && addOnboarding()} />
            <Button data-testid="add-onboarding-button" onClick={addOnboarding} className="bg-orange-500 hover:bg-orange-600"><Plus className="h-4 w-4" /></Button>
          </div>
        </Card>

        <Card icon={Wand2} title="Recurring work">
          <p className="text-sm text-slate-500">Annual assessments (to CNATs) and monthly 1-1 reviews (to RCSMs) auto-generate on a daily schedule.</p>
          <Button data-testid="generate-work-button" onClick={generateNow} disabled={generating} className="mt-3 bg-slate-900 hover:bg-slate-800">
            <Wand2 className={`mr-1.5 h-4 w-4 ${generating ? "animate-pulse" : ""}`} /> {generating ? "Generating…" : "Generate now"}
          </Button>
        </Card>

        <Card icon={Mail} title="Manager digests">
          <p className="text-sm text-slate-500">
            A daily email to RCSMs and CNATs summarising overdue reviews and training needing attention. Runs
            automatically each morning.
          </p>
          <div className="mt-3 flex items-center justify-between rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
            <div>
              <p className="font-semibold text-slate-700" data-testid="digest-recipient-count">
                {digests ? `${digests.recipients} manager(s) would be emailed now` : "…"}
              </p>
              <p>Last sent: {digests?.last_sent ? shortDate(digests.last_sent) : "never"}</p>
            </div>
            <Button data-testid="send-digests-button" onClick={sendDigests} disabled={sending} size="sm" className="bg-orange-500 hover:bg-orange-600">
              <Send className={`mr-1.5 h-4 w-4 ${sending ? "animate-pulse" : ""}`} /> {sending ? "Sending…" : "Send now"}
            </Button>
          </div>
          <p className="mt-2 text-xs text-slate-400">Emails are sent only to managers who have items needing attention.</p>
        </Card>

        <Card icon={BarChart3} title="Weekly compliance email">
          <p className="text-sm text-slate-500">
            An org-wide red/amber/green summary emailed to administrators every Monday morning for leadership oversight.
          </p>
          {compliance?.totals && (
            <div className="mt-3 flex gap-2 text-xs font-semibold">
              <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-emerald-700" data-testid="compliance-green">{compliance.totals.green} compliant</span>
              <span className="rounded-full bg-amber-50 px-2.5 py-1 text-amber-700" data-testid="compliance-amber">{compliance.totals.amber} attention</span>
              <span className="rounded-full bg-red-50 px-2.5 py-1 text-red-700" data-testid="compliance-red">{compliance.totals.red} at risk</span>
            </div>
          )}
          <div className="mt-3 flex items-center justify-between rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
            <div>
              <p className="font-semibold text-slate-700" data-testid="compliance-recipients">{compliance ? `${compliance.recipients} admin(s)` : "…"}</p>
              <p>Last sent: {compliance?.last_sent ? shortDate(compliance.last_sent) : "never"}</p>
            </div>
            <Button data-testid="send-compliance-button" onClick={sendCompliance} disabled={sendingComp} size="sm" className="bg-orange-500 hover:bg-orange-600">
              <Send className={`mr-1.5 h-4 w-4 ${sendingComp ? "animate-pulse" : ""}`} /> {sendingComp ? "Sending…" : "Send now"}
            </Button>
          </div>
        </Card>

        <Card icon={Plug} title="Integrations">
          <ul className="space-y-3">
            <IntegrationRow name="eLeaP (eLearning)" status="Simulated" />
            <IntegrationRow name="FLS (Field Leadership System)" status="Planned" />
          </ul>
          <div className="mt-4 flex items-center justify-between rounded-lg bg-slate-50 p-3">
            <div className="text-xs text-slate-500">
              <p className="font-semibold text-slate-700">eLeaP training sync</p>
              <p>Last synced: {eleap?.last_synced ? shortDate(eleap.last_synced) : "never"}</p>
            </div>
            <Button data-testid="eleap-sync-button" onClick={syncEleap} disabled={syncing} size="sm" className="bg-orange-500 hover:bg-orange-600">
              <RefreshCw className={`mr-1.5 h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync now"}
            </Button>
          </div>
          <p className="mt-3 text-xs text-slate-400">SIMULATED sync — live eLeaP/FLS API integrations pending.</p>
        </Card>
      </div>
    </div>
  );
}

function Card({ icon: Icon, title, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <Icon className="h-5 w-5 text-orange-500" />
        <h3 className="font-heading font-bold text-slate-900">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Chips({ items, onDelete, testid }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((it) => (
        <span key={it.id} data-testid={testid} className="flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700">
          {it.label}
          {it.deletable && <button onClick={() => onDelete(it.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-3 w-3" /></button>}
        </span>
      ))}
      {items.length === 0 && <span className="text-sm text-slate-400">None yet.</span>}
    </div>
  );
}

function AddRow({ placeholder, value, onChange, onAdd, testid }) {
  return (
    <div className="mt-4 flex gap-2">
      <Input data-testid={`new-${testid}-input`} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onAdd()} />
      <Button data-testid={`add-${testid}-button`} onClick={onAdd} className="bg-orange-500 hover:bg-orange-600"><Plus className="h-4 w-4" /></Button>
    </div>
  );
}

function IntegrationRow({ name, status }) {
  return (
    <li className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
      <span className="text-sm font-medium text-slate-800">{name}</span>
      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">{status}</span>
    </li>
  );
}
